// JOBE-0044.6: TypeRailBlock — middle block in the left rail. Reads
// the entity-types registry from EntityTypesContext (no local fetch);
// in tree mode also fetches /api/documents/kinds for the Document
// sub-tree.
//
// Click → append a `type:<slug>` chip via the chip vocabulary. Per R3,
// type selection is single-select; the parent merges-by-friendly-token
// so clicking a different type replaces the previous chip rather than
// adding a second one.

import { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { EntityTypesContext } from './EntityTypesContext.js';
import {
  DocumentKindsContext,
  DOCUMENT_KINDS_INITIAL,
} from './DocumentKindsContext.js';
import { RailRow } from './RailRow.jsx';

// SkeletonRows: 3 stand-in rows while the provider is in `loading`.
function SkeletonRows() {
  return (
    <ul className="library-rail-block-list" aria-busy="true">
      {[0, 1, 2].map((i) => (
        <li key={i}>
          <div className="library-rail-row library-rail-row-skeleton" aria-hidden="true">
            <span className="skel-line skel-w-80" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function ErrorState({ onRetry }) {
  return (
    <p className="library-rail-block-error">
      couldn't load ·{' '}
      <button
        type="button"
        className="library-rail-block-retry"
        onClick={onRetry}
      >
        retry
      </button>
    </p>
  );
}

function EmptyState() {
  return <p className="library-rail-block-empty">nothing</p>;
}

// useDocumentKinds — when the rail mounts under a DocumentKindsProvider
// (the LibraryListPage host), the hook short-circuits to the context
// value so the page and rail share a single fetch (JOBE-0044.10.2). The
// fallback local fetch stays for test-isolation callers that mount
// TypeRailBlock outside the provider (existing TypeRailBlock.test.jsx
// renders directly under EntityTypesContext.Provider only).
function useDocumentKinds(enabled) {
  const ctxValue = useContext(DocumentKindsContext);
  const providerMounted = ctxValue !== DOCUMENT_KINDS_INITIAL;

  const [state, setState] = useState({ status: 'idle', kinds: [], error: null });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (providerMounted) return undefined;
    if (!enabled) {
      setState({ status: 'idle', kinds: [], error: null });
      return undefined;
    }
    let alive = true;
    const ctrl = new AbortController();
    setState({ status: 'loading', kinds: [], error: null });
    fetch('/api/documents/kinds', {
      credentials: 'same-origin',
      signal: ctrl.signal,
    })
      .then(async (r) => {
        if (!alive) return;
        if (!r.ok) {
          setState({ status: 'error', kinds: [], error: `http_${r.status}` });
          return;
        }
        const j = await r.json();
        if (!alive) return;
        const kinds = Array.isArray(j?.kinds) ? j.kinds : [];
        setState({ status: 'ready', kinds, error: null });
      })
      .catch((err) => {
        if (!alive || err?.name === 'AbortError') return;
        setState({ status: 'error', kinds: [], error: err?.message || String(err) });
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [providerMounted, enabled, tick]);

  if (providerMounted) return ctxValue;

  const retry = () => { setTick((n) => n + 1); };
  return { ...state, retry };
}

export function TypeRailBlock({
  activeType,
  onPickType,
  typeRail,
  onChangeTypeRail,
}) {
  const ctx = useContext(EntityTypesContext);
  const isTree = typeRail === 'tree';
  const documentKinds = useDocumentKinds(isTree);

  // Tree mode's Document sub-tree expand state — component-local; not
  // persisted (spec §"Tap targets").
  const [documentOpen, setDocumentOpen] = useState(false);

  const handlePickType = (kind) => {
    if (onPickType) onPickType(kind);
  };

  const handleRetry = () => {
    // The provider doesn't expose a retry seam in v1 (route remount
    // re-issues the fetch); the rail-level retry button points at the
    // user gesture that does the same: reload. We do a soft retry by
    // bumping the route via location.reload as a last resort, but the
    // simpler v1 affordance is to do nothing and let the page-level
    // banner from JOBE-0044.14 own the retry surface. For now, this
    // hook is a no-op placeholder so the markup carries the retry
    // button shape per the rail-block degradation rules.
  };

  return (
    <section className="library-rail-block" aria-label="Type rail">
      <header className="library-rail-block-header">
        <h3 className="library-rail-block-title">Types</h3>
        <div
          className="library-rail-block-mode"
          role="group"
          aria-label="Type rail presentation"
        >
          <button
            type="button"
            className={
              `library-rail-block-mode-btn` +
              (typeRail === 'flat' ? ' library-rail-block-mode-btn-active' : '')
            }
            onClick={() => onChangeTypeRail && onChangeTypeRail('flat')}
            aria-pressed={typeRail === 'flat'}
          >
            flat
          </button>
          <button
            type="button"
            className={
              `library-rail-block-mode-btn` +
              (typeRail === 'tree' ? ' library-rail-block-mode-btn-active' : '')
            }
            onClick={() => onChangeTypeRail && onChangeTypeRail('tree')}
            aria-pressed={typeRail === 'tree'}
          >
            tree
          </button>
        </div>
      </header>

      {ctx.status === 'loading' && <SkeletonRows />}
      {ctx.status === 'error' && <ErrorState onRetry={handleRetry} />}
      {ctx.status === 'ready' && ctx.types.length === 0 && <EmptyState />}

      {ctx.status === 'ready' && ctx.types.length > 0 && (
        <ul className="library-rail-block-list">
          {isTree && (
            <li>
              <RailRow
                glyph="·"
                label="All entities"
                count={ctx.types.reduce((sum, t) => sum + (t.count || 0), 0)}
                onClick={() => onPickType && onPickType(null)}
              />
            </li>
          )}
          {ctx.types.map((t) => {
            const isDocumentBranch =
              isTree && t.structuralShape === 'document';
            return (
              <li key={t.kind}>
                <RailRow
                  glyph="·"
                  label={t.displayName}
                  pill={{ kind: t.kindSource, label: t.kindSource }}
                  count={t.count}
                  selected={activeType === t.kind}
                  chevron={
                    isDocumentBranch
                      ? (documentOpen ? '▾' : '▸')
                      : null
                  }
                  onClick={() => {
                    if (isDocumentBranch) {
                      setDocumentOpen((open) => !open);
                    } else {
                      handlePickType(t.kind);
                    }
                  }}
                />
                {isDocumentBranch && documentOpen && (
                  <ul className="library-rail-block-list library-rail-block-sublist">
                    {documentKinds.status === 'loading' && <SkeletonRows />}
                    {documentKinds.status === 'error' && (
                      <li>
                        <ErrorState onRetry={documentKinds.retry} />
                      </li>
                    )}
                    {documentKinds.status === 'ready' &&
                      documentKinds.kinds.length === 0 && (
                        <li><EmptyState /></li>
                      )}
                    {documentKinds.status === 'ready' &&
                      documentKinds.kinds.map((k) => (
                        <li key={k.kind}>
                          <RailRow
                            glyph="·"
                            label={k.kind}
                            pill={{ kind: k.kindSource, label: k.kindSource }}
                            count={k.count}
                            selected={activeType === k.kind}
                            onClick={() => handlePickType(k.kind)}
                          />
                        </li>
                      ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {ctx.status === 'ready' && (
        <Link
          to="/entities"
          className="library-rail-block-browse-all"
          data-testid="library-rail-browse-all-link"
        >
          Browse all entity types →
        </Link>
      )}
    </section>
  );
}
